import React from 'react'
import { Box, styled, Typography } from '@mui/material'
import { DeleteOutlined, DragIndicatorOutlined } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { CanvasItemCard, DeleteButton, DragHandle, CanvasItemIcon } from './DesignerCanvasSection.styles'
import { IFunctionComponentInstance, ITextStyles, textAlignmentToCss } from 'src/types/report-designer'
import { FunctionType } from 'src/enums'

export interface ICanvasFunctionItemProps {
  item: IFunctionComponentInstance
  componentIcon: React.ReactNode
  isSelected: boolean
  accentColor: string
  isDragging: boolean
  isDragOver: boolean
  onSelectItem: (id: string) => void
  onDeleteItem: (id: string) => void
  onDragStart: (e: React.DragEvent) => void
  onDragOver: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent) => void
  onDragEnd: () => void
}

export const CanvasFunctionItem: React.FC<ICanvasFunctionItemProps> = ({
  item,
  componentIcon,
  isSelected,
  accentColor,
  isDragging,
  isDragOver,
  onSelectItem,
  onDeleteItem,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}) => {
  const { t: translate } = useTranslation(['dynamic-report'])
  const functionTypeDisplay = item.functionType != null ? FunctionType[item.functionType] : 'Type'
  const formattedDisplay = `${item.label} (${functionTypeDisplay}) = 0`
  const styles = item.styles

  return (
    <CanvasItemCard
      key={item.id}
      isSelected={isSelected}
      accentColor={accentColor}
      isDragging={isDragging}
      isDragOver={isDragOver}
      onClick={() => onSelectItem(item.id)}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      <DragHandle>
        <DragIndicatorOutlined fontSize="small" />
      </DragHandle>
      <CanvasItemIcon accentColor={accentColor}>{componentIcon}</CanvasItemIcon>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <StyledTypography variant="body2" styles={styles as ITextStyles}>
          {formattedDisplay}
        </StyledTypography>
      </Box>
      <DeleteButton
        size="small"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation()
          onDeleteItem(item.id)
        }}
        title={translate('delete-item', { ns: 'dynamic-report' })}
      >
        <DeleteOutlined fontSize="small" />
      </DeleteButton>
    </CanvasItemCard>
  )
}

interface IStyledTypographyProps {
  styles: ITextStyles
}
const StyledTypography = styled(Typography)<IStyledTypographyProps>(({ styles }) => ({
  fontFamily: styles?.fontFamily ?? 'arial',
  color: styles?.fontColor ?? 'text.primary',
  textAlign: textAlignmentToCss(styles?.alignment),
  lineHeight: styles?.lineHeight ?? 1.4,
  letterSpacing: styles?.letterSpacing ? `${styles.letterSpacing}px` : 'normal',
  fontSize: styles?.fontSize ? `${styles.fontSize}px` : '14px',
  fontWeight: styles?.bold ? 700 : 400,
}))
